import { useEffect, useRef } from 'react';
import { PageAction } from '@core/pubsub/EventBus';
import { usePageActionContext } from '@core/contexts/PageActionContext';

// Register Action Pattern
// Core registration hook. Registers any async action under a named PageAction key.
// On mount  → registers the action slot (button appears in NewPageTitleBar).
// On unmount → clears the slot (button disappears, function reference released).
//
// Usage for specific pages:
// shows overlay (default — nothing changes for existing pages)
// useRegisterAction(PageAction.EXPORT, exportFn, isDisabled);
// silent action — button has its own spinner but no global overlay
// useRegisterAction(PageAction.EXPORT, exportFn, isDisabled, false);
export const useRegisterAction = (
    key: PageAction,
    action: () => Promise<void>,
    disabled?: boolean,
    showOverlay: boolean = true
) => {
    const { registerAction, setActionDisabled } = usePageActionContext();

    const actionRef = useRef(action);
    actionRef.current = action;

    useEffect(() => {
        const stableAction = () => actionRef.current();
        registerAction(key, stableAction, disabled ?? false, showOverlay);
        return () => {
            registerAction(key, null, false, true);
        };
    }, [key, showOverlay, registerAction]);

    useEffect(() => {
        setActionDisabled(key, disabled ?? false);
    }, [key, disabled]);
};

// Register For Refresh Actions
// Add here so that all pages which extend SecurePage can use the same hook.
export const useRegisterRefresh = (action: () => Promise<void>, disabled?: boolean) =>
    useRegisterAction(PageAction.REFRESH, action, disabled, false);
